import { ChangeEvent, ForwardedRef, forwardRef, useState } from 'react';
import { Box, FormHelperText, TextField, TextFieldProps } from '@mui/material';
import clsx from 'clsx';
import { Control, Controller, ControllerProps } from 'react-hook-form';
import { ErrorFormField } from '../types';

type BankCardInputProps = TextFieldProps &
  ErrorFormField & {
    maxCardLength?: number; // default 19
    spaceNumber?: number; // default 4, black space
    onValueChange?: (str: string) => void;
  };

const BankCardInput = forwardRef((props: BankCardInputProps, ref: ForwardedRef<HTMLDivElement>) => {
  const { className, spaceNumber = 4, maxCardLength = 19, value, onValueChange, error, errMessage, ...extraProps } = props;
  /**
   * methods
   */
  const generateNewBlankSpaceValue = (value = '') => {
    const replaceInput = value.replace(/\D/g, '');
    let newValue = '';
    for (let i = 0; i < replaceInput.length; i++) {
      newValue += replaceInput[i];
      if ((i + 1) % spaceNumber === 0 && i !== replaceInput.length - 1) {
        newValue += ' ';
      }
    }

    return newValue;
  };
  const [inputValue, setInputValue] = useState(onValueChange ? generateNewBlankSpaceValue(value as string) : '');

  const valueChangeAction = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = generateNewBlankSpaceValue(e.target.value);
    setInputValue(newValue);
    if (onValueChange) onValueChange(newValue.replace(/\s/g, ''));
  };

  return (
    <Box className={clsx(className)}>
      <TextField
        {...extraProps}
        ref={ref}
        className="w-full"
        value={inputValue}
        onChange={valueChangeAction}
        InputProps={{
          inputProps: { maxLength: maxCardLength },
        }}
        variant="outlined"
      />
      {error && (
        <FormHelperText className="mt-1" error={error}>
          {errMessage}
        </FormHelperText>
      )}
    </Box>
  );
});

type ControlledProps = Omit<ControllerProps, 'control' | 'render'> &
  BankCardInputProps & {
    control: Control<any>;
  };

export const BankCardInputControlled = forwardRef(({ control, name, ...props }: ControlledProps, ref: ForwardedRef<HTMLDivElement>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        return <BankCardInput {...props} ref={ref} value={value} onValueChange={onChange} error={!!error} errMessage={error?.message} />;
      }}
    />
  );
});

export default BankCardInput;
